// src/pages/instances/instance-dashboard/InstanceDashboard.jsx
import React, { useContext, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, Grid, Tooltip, Chip } from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import BarChartIcon from "@mui/icons-material/BarChart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { AuthContext } from "../../../../helpers/AuthContext";
import ImportHistoryDialog from "../../settings/global-settings/tools/ImportHistoryDialog";
import PageHeaderWithBreadcrumbs from "../../../../components/PageHeaderWithBreadcrumbs";

const gradients = {
  green: {
    card:
      "linear-gradient(135deg, rgba(16,185,129,0.22) 0%, rgba(5,150,105,0.18) 50%, rgba(6,95,70,0.16) 100%)",
    icon:
      "radial-gradient(120px 60px at 20% 20%, rgba(16,185,129,0.35), transparent), radial-gradient(120px 60px at 80% 40%, rgba(5,150,105,0.25), transparent)",
    ring:
      "conic-gradient(from 0deg, rgba(16,185,129,0.28), transparent, rgba(34,197,94,0.22), transparent, rgba(5,150,105,0.22), transparent)",
  },
  blue: {
    card:
      "linear-gradient(135deg, rgba(59,130,246,0.22) 0%, rgba(29,78,216,0.18) 50%, rgba(30,64,175,0.16) 100%)",
    icon:
      "radial-gradient(120px 60px at 20% 20%, rgba(59,130,246,0.35), transparent), radial-gradient(120px 60px at 80% 40%, rgba(29,78,216,0.25), transparent)",
    ring:
      "conic-gradient(from 0deg, rgba(59,130,246,0.28), transparent, rgba(99,102,241,0.22), transparent, rgba(29,78,216,0.22), transparent)",
  },
  purple: {
    card:
      "linear-gradient(135deg, rgba(139,92,246,0.22) 0%, rgba(124,58,237,0.18) 50%, rgba(91,33,182,0.16) 100%)",
    icon:
      "radial-gradient(120px 60px at 20% 20%, rgba(139,92,246,0.35), transparent), radial-gradient(120px 60px at 80% 40%, rgba(124,58,237,0.25), transparent)",
    ring:
      "conic-gradient(from 0deg, rgba(139,92,246,0.28), transparent, rgba(168,85,247,0.22), transparent, rgba(124,58,237,0.22), transparent)",
  },
  orange: {
    card:
      "linear-gradient(135deg, rgba(251,146,60,0.22) 0%, rgba(234,88,12,0.18) 50%, rgba(194,65,12,0.16) 100%)",
    icon:
      "radial-gradient(120px 60px at 20% 20%, rgba(251,146,60,0.35), transparent), radial-gradient(120px 60px at 80% 40%, rgba(234,88,12,0.25), transparent)",
    ring:
      "conic-gradient(from 0deg, rgba(251,146,60,0.28), transparent, rgba(245,158,11,0.22), transparent, rgba(234,88,12,0.22), transparent)",
  },
  amber: {
    card:
      "linear-gradient(135deg, rgba(255,251,235,0.98) 0%, rgba(254,243,199,0.92) 50%, rgba(251,191,36,0.18) 100%)",
    icon:
      "radial-gradient(120px 60px at 20% 20%, rgba(245,158,11,0.28), transparent), radial-gradient(120px 60px at 80% 40%, rgba(234,88,12,0.20), transparent)",
    ring:
      "conic-gradient(from 0deg, rgba(245,158,11,0.30), transparent, rgba(251,146,60,0.22), transparent, rgba(217,119,6,0.24), transparent)",
  },
  red: {
    card:
      "linear-gradient(135deg, rgba(254,242,242,0.98) 0%, rgba(254,226,226,0.92) 50%, rgba(239,68,68,0.18) 100%)",
    icon:
      "radial-gradient(120px 60px at 20% 20%, rgba(239,68,68,0.30), transparent), radial-gradient(120px 60px at 80% 40%, rgba(220,38,38,0.24), transparent)",
    ring:
      "conic-gradient(from 0deg, rgba(239,68,68,0.28), transparent, rgba(248,113,113,0.22), transparent, rgba(220,38,38,0.22), transparent)",
  },
};

const ReportCard = ({
  title,
  subtitle,
  Icon,
  color = "green",
  disabled = false,
  badge,
  onClick,
}) => {
  const theme = gradients[color];

  const card = (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        position: "relative",
        width: "100%",
        boxSizing: "border-box",
        p: { xs: 3, md: 3.5 },
        borderRadius: "24px",
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
        outline: "none",
        background: theme.card,
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.22)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.06)",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: "-1px",
          borderRadius: "24px",
          background: theme.ring,
          mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "1px",
          pointerEvents: "none",
          filter: "blur(6px)",
        },
        transition:
          "transform 240ms cubic-bezier(.2,.8,.2,1), opacity 200ms ease, box-shadow 240ms ease",
        opacity: 0.9,
        "&:hover": disabled
          ? {}
          : {
              transform: "translateY(-4px) rotate3d(1, -1, 0, 4deg)",
              opacity: 1,
              boxShadow:
                "0 16px 40px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.10)",
            },
        "& .sparkle": {
          position: "absolute",
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "rgba(255,215,0,0.8)",
          filter: "blur(0.4px)",
          animation: "floatSparkle 2800ms ease-in-out infinite",
        },
        "@keyframes floatSparkle": {
          "0%": { transform: "translateY(0) scale(1)", opacity: 0.9 },
          "50%": { transform: "translateY(-6px) scale(1.15)", opacity: 0.4 },
          "100%": { transform: "translateY(0) scale(1)", opacity: 0.9 },
        },
      }}
    >
      {/* sparkles */}
      <Box className="sparkle" sx={{ top: 10, right: 18 }} />
      <Box className="sparkle" sx={{ bottom: 12, left: 24, animationDelay: "400ms" }} />
      <Box className="sparkle" sx={{ top: 26, left: 34, animationDelay: "900ms" }} />

      {/* icon */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 72,
          height: 72,
          borderRadius: "20px",
          mb: 2,
          background: theme.icon,
          boxShadow: "inset 0 0 18px rgba(255,255,255,0.12)",
        }}
      >
        <Icon sx={{ fontSize: 42 }} />
      </Box>

      {/* text */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ fontSize: 18, fontWeight: 700, letterSpacing: 0.2 }}>{title}</Box>
        <Chip
          size="small"
          label={badge ?? (disabled ? "Coming soon" : "Open")}
          sx={{
            bgcolor: disabled ? "rgba(255,215,0,0.14)" : "rgba(0,0,0,0.06)",
            border: disabled ? "1px solid rgba(255,215,0,0.45)" : "1px solid rgba(0,0,0,0.15)",
            color: "#777",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
          }}
        />
      </Box>
      <Box sx={{ mt: 0.5, fontSize: 13.5, opacity: 0.85 }}>{subtitle}</Box>
    </Box>
  );

  return disabled ? (
    <Tooltip title="This report is coming soon ✨" placement="top" arrow>
      <span style={{ display: "block", width: "100%" }}>{card}</span>
    </Tooltip>
  ) : (
    card
  );
};

const InstanceDashboard = () => {
  const { instanceUniqueId } = useParams();
  const history = useHistory();
  const { authState } = useContext(AuthContext);
  const [importHistoryOpen, setImportHistoryOpen] = useState(false);
  const role = String(authState?.role || "").trim().toLowerCase().replace(/_/g, "-");
  const canViewImportHistory = ["administrator", "admin", "superadmin", "super-administrator"].includes(role);

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <div className="options-container fade-in-effect-usability">
          <div className="options-inner-container">
            <PageHeaderWithBreadcrumbs
              title="Reporting Dashboard"
              breadcrumb="Reporting Dashboard"
              includeButton={false}
            />

            {/* Uniform outer padding for the tile grid */}
            <Box sx={{ mt: 2, mb: 3, px: { xs: 0, md: 0 } }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Commission"
                    subtitle="Track booked vs billed commission and payouts"
                    Icon={MonetizationOnIcon}
                    color="green"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/commissions`)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Sales Rep"
                    subtitle="Rep revenue, margin, trends, period compares"
                    Icon={BarChartIcon}
                    color="blue"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/sales-rep`)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Documents"
                    subtitle="Sales order, production, PO, and invoice activity"
                    Icon={BarChartIcon}
                    color="blue"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/documents`)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Product Sold"
                    subtitle="Product revenue, volume, profit, suppliers, and reps"
                    Icon={Inventory2Icon}
                    color="red"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/product-sold`)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Supplier"
                    subtitle="Spend by supplier, lead times, variance"
                    Icon={LocalShippingIcon}
                    color="purple"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/supplier`)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Supplier Bill Pipeline"
                    subtitle="Expected, outstanding, partial, and completed supplier billing"
                    Icon={ReceiptLongIcon}
                    color="amber"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/supplier-bill-pipeline`)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Client Invoicing"
                    subtitle="View orders and projects ready for client invoicing, including progress, invoice readiness, and estimated invoice amount."
                    Icon={ReceiptLongIcon}
                    color="orange"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/client-invoicing`)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Work in Progress"
                    subtitle="Open projects, workflow stage, due dates, and blockers"
                    Icon={AssignmentTurnedInIcon}
                    color="green"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/work-in-progress`)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="A/R Aging"
                    subtitle="Review outstanding receivables by aging bucket, customer, and representative."
                    Icon={ReceiptLongIcon}
                    color="amber"
                    onClick={() =>
                      history.push(`/instance/${instanceUniqueId}/reports/ar-aging`)
                    }
                  />
                </Grid>
                {canViewImportHistory && <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Import Activity"
                    subtitle="Review all import activity, importing users, results and downloadable reports"
                    Icon={HistoryOutlinedIcon}
                    color="blue"
                    onClick={() => setImportHistoryOpen(true)}
                  />
                </Grid>}
              </Grid>
              <ImportHistoryDialog
                open={importHistoryOpen}
                onClose={() => setImportHistoryOpen(false)}
                instanceUniqueId={instanceUniqueId}
              />
            </Box>

            {/* Keep your data table for now (commented)
            <DataDashboard data={[]} columns={[]} type="Reporting" />
            */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstanceDashboard;
